import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ClipboardCheck, ShieldCheck } from 'lucide-react';
import { getErrorMessage } from '../utils/apiError';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const OperatorLogin = () => {
  const navigate = useNavigate();
  const [operators, setOperators] = useState([]);
  const [operatorId, setOperatorId] = useState('');
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/maintenance-checklist/operators`)
      .then((r) => setOperators(r.data || []))
      .catch((e) => toast.error(getErrorMessage(e, 'Failed to load operators')));
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!operatorId || !pin) {
      toast.error('Please select your name and enter your 4-digit PIN');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/maintenance-checklist/operator-login`, {
        operator_id: operatorId,
        phone_last4: pin,
      });
      if (res.data.success) {
        localStorage.setItem('njk_operator', JSON.stringify(res.data.operator));
        toast.success(`Welcome, ${res.data.operator.full_name}`);
        navigate('/operator/checklist');
      }
    } catch (err) {
      toast.error(getErrorMessage(err, 'Login failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 to-slate-700 px-4 py-12">
      <Card className="w-full max-w-md shadow-2xl">
        <CardHeader className="text-center">
          <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-3">
            <ClipboardCheck className="w-8 h-8 text-blue-600" />
          </div>
          <CardTitle className="text-2xl">Operator Login</CardTitle>
          <p className="text-sm text-gray-500">Lorry Crane Maintenance Checklist</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <Label>Select Your Name *</Label>
              <Select value={operatorId} onValueChange={setOperatorId}>
                <SelectTrigger data-testid="operator-select">
                  <SelectValue placeholder="-- Choose --" />
                </SelectTrigger>
                <SelectContent>
                  {operators.map((op) => (
                    <SelectItem key={op.id} value={op.id}>
                      {op.full_name} — {op.designation}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {operators.length === 0 && (
                <p className="text-xs text-orange-600 mt-1">
                  No operators set up yet. Admin must add you under Employee Records first.
                </p>
              )}
            </div>
            <div>
              <Label>PIN (last 4 digits of your phone number)</Label>
              <Input type="password" inputMode="numeric" maxLength={4}
                     value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
                     placeholder="••••" data-testid="operator-pin" />
            </div>
            <Button type="submit" disabled={loading} className="w-full" data-testid="operator-login-btn">
              <ShieldCheck size={16} className="mr-2" /> {loading ? 'Signing in...' : 'Sign In'}
            </Button>
            <p className="text-xs text-gray-400 text-center pt-2">
              Forgot PIN? Ask your supervisor — it's the last 4 digits of your registered phone number.
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default OperatorLogin;
